import { type BidRoute, getBidRoute } from './bid-route.js';
import type { AuctisyncSupabase } from './client.js';

/**
 * Where a sale's LIVE updates come from — the one decision every live page
 * asks for and none makes for itself (2026-10-08).
 *
 * A sale on the engine is live through the engine's feed (`createSaleFeed`).
 * A sale on the Supabase book — the parachute, fixed by its first bid
 * (memory: feedback_engine_is_the_default_supabase_is_the_parachute) — keeps
 * today's Realtime path (`subscribeToLots`). The answer is the bid path's own
 * (`bid-route.ts`, `public.get_bid_path`), so the place a page reads prices
 * from and the place its bids go can never disagree.
 *
 * There is NO automatic fallback from the engine to Realtime here. Jim,
 * 2026-10-08, moved all live work to the engine and left the fallback to the
 * backup design (memory: project_engine_owns_all_live_work). An engine sale
 * whose feed is down shows the feed's own `reconnecting` / `paused`.
 *
 * SIGNED-OUT VISITORS. Protocol 2 lets a signed-out visitor watch an engine
 * sale as a public observer, and since migration 20261008170000 anon may call
 * `get_bid_path` (for a lot its policies let it read), so a signed-out visitor
 * is routed exactly like a bidder. A database without that migration still
 * refuses anon (42501): the visitor is then routed to Realtime and the answer
 * SAYS so (`reason: 'signed_out'`) rather than passing for a fact about the
 * sale. That fallback disappears with the migration; no page changes.
 */
export type LiveSource =
  | {
      kind: 'engine';
      saleId: string;
      houseId: string;
      engineUrl: string;
      engineAuctionId: string;
    }
  | {
      kind: 'supabase';
      /**
       * `sale_on_supabase` — the sale's bids are in the `place_bid` ledger.
       * `signed_out` — the route could not be asked; see above.
       */
      reason: 'sale_on_supabase' | 'signed_out';
    };

export type LiveSourceLookup =
  | { ok: true; source: LiveSource }
  | { ok: false; error: { message: string; code?: string } };

/** The live source for a bid route. Pure. */
export function liveSourceForRoute(
  route: BidRoute,
  opts: { signedOut?: boolean } = {},
): LiveSource {
  if (route.path === 'engine') {
    return {
      kind: 'engine',
      saleId: route.saleId,
      houseId: route.houseId,
      engineUrl: route.engineUrl,
      engineAuctionId: route.engineAuctionId,
    };
  }
  return { kind: 'supabase', reason: opts.signedOut === true ? 'signed_out' : 'sale_on_supabase' };
}

/**
 * The live source for the sale a lot belongs to. Any lot of the sale answers
 * for all of it (the path is per sale); a Lots page passes any one of its
 * lots. `fresh` skips the route cache, as `getBidRoute` does.
 *
 * A failed lookup is returned as such, never guessed: the page says it is
 * reconnecting and asks again.
 */
export async function resolveLiveSource(
  supabase: AuctisyncSupabase,
  lotId: string,
  opts: { fresh?: boolean } = {},
): Promise<LiveSourceLookup> {
  const lookup = await getBidRoute(supabase, lotId, opts);
  if (!lookup.ok) return lookup;
  return {
    ok: true,
    source: liveSourceForRoute(lookup.route, { signedOut: lookup.signedOut === true }),
  };
}
